import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, Tag, User } from 'lucide-react';
import ContestBanner from './ContestBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return ''; }
};

const FADE_UP = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] },
};

export default function BlogPostPage({ slug }) {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_URL}/api/blog/posts/${encodeURIComponent(slug)}`)
      .then((r) => {
        if (r.status === 404) throw new Error('NOT_FOUND');
        if (!r.ok) throw new Error(`Server returned ${r.status}`);
        return r.json();
      })
      .then((data) => { if (!cancelled) setPost(data); })
      .catch((e) => { if (!cancelled) setErr(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-['Outfit']">
      <ContestBanner />

      <div className="max-w-3xl mx-auto px-6 pt-6">
        <a
          href="/blog"
          data-testid="blog-post-back-link"
          className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-[#FF5E1A] transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          All posts
        </a>
      </div>

      {loading && (
        <div className="max-w-3xl mx-auto px-6 py-20 text-zinc-500 text-sm font-['JetBrains_Mono']" data-testid="blog-post-loading">
          Loading post…
        </div>
      )}

      {err === 'NOT_FOUND' && (
        <div className="max-w-3xl mx-auto px-6 py-20" data-testid="blog-post-not-found">
          <h1 className="text-3xl font-black mb-3">Post not found</h1>
          <p className="text-zinc-400 mb-6">The post you&apos;re looking for might have been moved or unpublished.</p>
          <a href="/blog" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white text-xs font-bold tracking-[0.12em] uppercase rounded-sm transition-all">
            Back to blog
          </a>
        </div>
      )}
      {err && err !== 'NOT_FOUND' && (
        <div className="max-w-3xl mx-auto px-6 py-20 text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-sm p-4">{err}</div>
      )}

      {post && (
        <article className="max-w-3xl mx-auto px-6 pt-10 pb-20" data-testid="blog-post-article">
          <motion.header {...FADE_UP} className="mb-10 pb-8 border-b border-[#27272A]">
            <div className="flex flex-wrap items-center gap-3 text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.18em] text-zinc-500 mb-4">
              <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {fmtDate(post.published_at)}</span>
              <span className="text-zinc-700">·</span>
              <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {post.read_minutes || 5} min read</span>
              {post.author && (
                <>
                  <span className="text-zinc-700">·</span>
                  <span className="flex items-center gap-1.5"><User className="w-3 h-3" /> {post.author}</span>
                </>
              )}
            </div>
            <h1 className="font-black tracking-tighter leading-[1.05] text-3xl sm:text-4xl lg:text-5xl mb-4">
              {post.title}
            </h1>
            {post.excerpt && (
              <p className="text-lg text-zinc-300 leading-relaxed">{post.excerpt}</p>
            )}
            {(post.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-5">
                {post.tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#111111] border border-[#27272A] rounded-sm text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-400">
                    <Tag className="w-2.5 h-2.5" /> {t}
                  </span>
                ))}
              </div>
            )}
          </motion.header>

          <motion.div
            {...FADE_UP}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="prose-blog space-y-6 text-[17px] leading-[1.8] text-zinc-300"
          >
            <ReactMarkdown
              components={{
                h2: ({ node, ...props }) => (
                  <h2 className="font-black tracking-tight text-2xl sm:text-3xl text-white mt-12 mb-4" {...props} />
                ),
                h3: ({ node, ...props }) => (
                  <h3 className="font-bold text-xl text-white mt-8 mb-3" {...props} />
                ),
                p: ({ node, ...props }) => <p className="leading-[1.8]" {...props} />,
                a: ({ node, ...props }) => <a className="text-[#FF5E1A] hover:text-[#FF7A42] underline underline-offset-4" {...props} />,
                strong: ({ node, ...props }) => <strong className="text-white font-bold" {...props} />,
                code: ({ node, inline, ...props }) =>
                  inline
                    ? <code className="px-1.5 py-0.5 bg-[#111111] border border-[#27272A] rounded-sm text-[#FF5E1A] text-[14px] font-['JetBrains_Mono']" {...props} />
                    : <code className="block p-4 bg-[#0A0A0A] border border-[#27272A] rounded-sm text-zinc-300 text-[14px] font-['JetBrains_Mono'] overflow-x-auto" {...props} />,
                ul: ({ node, ...props }) => <ul className="list-disc pl-6 space-y-2 marker:text-[#FF5E1A]" {...props} />,
                ol: ({ node, ...props }) => <ol className="list-decimal pl-6 space-y-2 marker:text-[#FF5E1A]" {...props} />,
                li: ({ node, ...props }) => <li className="leading-[1.7]" {...props} />,
                blockquote: ({ node, ...props }) => (
                  <blockquote className="border-l-2 border-[#FF5E1A] pl-5 italic text-zinc-400 my-6" {...props} />
                ),
                table: ({ node, ...props }) => (
                  <div className="overflow-x-auto my-6">
                    <table className="w-full text-sm border border-[#27272A] rounded-sm" {...props} />
                  </div>
                ),
                thead: ({ node, ...props }) => <thead className="bg-[#0A0A0A] text-[10px] uppercase tracking-wider font-['JetBrains_Mono']" {...props} />,
                th: ({ node, ...props }) => <th className="text-left px-3 py-2 border-b border-[#27272A] text-zinc-300" {...props} />,
                td: ({ node, ...props }) => <td className="px-3 py-2 border-t border-[#1a1a1a] align-top" {...props} />,
                hr: () => <hr className="my-10 border-[#27272A]" />,
              }}
            >
              {post.body}
            </ReactMarkdown>
          </motion.div>

          {/* CTA */}
          <div className="mt-16 pt-10 border-t border-[#27272A]">
            <div className="bg-gradient-to-br from-[#FF5E1A]/10 via-[#0F0F0F] to-[#0F0F0F] border border-[#FF5E1A]/30 rounded-sm p-6 sm:p-8" data-testid="blog-cta">
              <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-2">// Ready to tune YOUR car?</div>
              <h3 className="text-2xl sm:text-3xl font-black tracking-tight mb-3">Start a tune from $250.</h3>
              <p className="text-zinc-400 mb-5 max-w-xl">
                Unlimited revisions until YOU Green-Light it. AI Copilot scores every datalog. Stock backup archived forever.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/green-light#pricing"
                  data-testid="blog-cta-pricing"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white text-xs font-bold tracking-[0.12em] uppercase rounded-sm transition-all"
                >
                  See Pricing
                </a>
                <a
                  href="/blog"
                  data-testid="blog-cta-more"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#27272A] hover:bg-[#3a3a3d] text-white text-xs font-bold tracking-[0.12em] uppercase rounded-sm transition-all"
                >
                  More posts
                </a>
              </div>
            </div>
          </div>
        </article>
      )}
    </div>
  );
}
